/* eslint-disable react-hooks/rules-of-hooks */
import React, { createContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../app";
import { getLocalizedURL } from "../localization";
import { backendHost, frontendHost, openExternalLink } from "../urls";

interface User {
  email: string;
  firstName: string;
  lastName: string;
  token: string;
}

interface State {
  isLoggedIn: boolean;
  user: User | null;
}

interface CloudProject {
  id: string;
  name: string;
  timeCreated: string
}

const initialState: State = {
  isLoggedIn: false,
  user: null
};

const RESPONSE_HEADERS = ["st-access-token", "st-refresh-token"];
const appCodeRefreshRate = 2000; // 2 seconds
const appCodeMaxAttempts = 100;

export const AuthContext = createContext({
  ...initialState,
  logIn: () => Promise.resolve(true),
  logOut: () => {},
  /** Grab all cloud projects that we have access to */
  getProjects: () => Promise.resolve([] as CloudProject[]),
  /** Check if this project exists in the cloud, using the original project id */
  checkProject: (originalID: string) => Promise.resolve(true),
  /** Get the static.json config file for a project, using the cloud project id */
  getConfig: (projectID: string) => Promise.resolve()
});

const getAccessToken = () => {
  return localStorage.getItem("st-access-token");
};

const getRefreshToken = () => {
  return localStorage.getItem("st-refresh-token");
};

export const AuthProvider = (props) => {
  const { i18n, t } = useTranslation();
  const [state, setState] = useState<State>(initialState);

  const initialize = async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return logOut(false);

    try {
      const url = `${backendHost}/auth/session/refresh`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          'rid': 'session',
          'Authorization': `Bearer ${refreshToken}`
        }
      });

      if (response.status === 200) {
        setTokens(response.headers);
        setUserInfo();
        return;
      }
    } catch {
      console.log("Error refreshing token");
    }

    logOut(false);
  };

  const logIn = async () => {
    try {
      const appCode = await getAppCode();
      const url = getLocalizedURL(
        `${frontendHost}/link?code=${appCode}`,
        i18n.language
      );

      openExternalLink(url);
      return useAppCode(appCode);
    } catch {
      toast.error(t('user-account.error_linking_account'));
      return false;
    }
  };

  const logOut = (showMessage=true) => {
    setState({...initialState});
    clearTokens();

    if (!showMessage) return;
    toast.success(t('user-account.account_unlinked'));
  };

  const getConfig = async (projectID) => {
    const accessToken = getAccessToken();
    const url = `${frontendHost}/viewer/${projectID}/static.json`;
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });

    const data = await response.json();
    return data;
  };

  const checkProject = async (originalID): Promise<boolean> => {
    try {
      const accessToken = getAccessToken();
      const url = `${backendHost}/api/projects/${originalID}/exists`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      return response.status === 200;
    } catch {
      return false;
    }
  };

  const getProjects = async (): Promise<CloudProject[]> => {
    try {
      const accessToken = getAccessToken();
      const url = `${backendHost}/api/projects`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();

      return data.map(project => {
        return {
          id: project.id,
          name: project.name as string,
          timeCreated: project.timeCreated as string
        } as CloudProject;
      });
    } catch {
      return [];
    }
  };

  const getAppCode = async () => {
    const url = `${backendHost}/api/app_codes/request`;
    const response = await fetch(url);
    const data = await response.json();
    const appCode = String(data.access_code);
    return appCode;
  };

  /** Consume app code until it returns a success code */
  const useAppCode = async (appCode: string): Promise<boolean> => {
    if (!appCode) return true;

    return new Promise((resolve, reject) => {
      let retryAttempt = 0;

      const interval = setInterval(async () => {
        if (retryAttempt >= appCodeMaxAttempts) {
          resolve(false);
          clearInterval(interval);
          toast.error(t('user-account.error_linking_account'));
          return;
        };

        const url = `${backendHost}/api/app_codes/consume/${appCode}`;
        const response = await fetch(url);
        const success = response.status === 200;

        if (success) {
          setTokens(response.headers);
          setUserInfo();
          toast.success(t('user-account.account_linked'));
          clearInterval(interval);
          resolve(true);
        }

        retryAttempt += 1;
      }, appCodeRefreshRate);
    });
  };

  const getUserInfo = async () => {
    const accessToken = getAccessToken();
    const url = `${backendHost}/api/users/me`;
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });

    const data = await response.json();

    return {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      token: accessToken
    } as User;
  };

  const setUserInfo = async () => {
    const user = await getUserInfo();

    setState({
      ...initialState,
      user,
      isLoggedIn: true
    });
  };

  const setTokens = (headers) => {
    [...headers].forEach(([key, value]) => {
      if (!RESPONSE_HEADERS.includes(key)) return;
      localStorage.setItem(key, value);
    });
  };

  const clearTokens = () => {
    RESPONSE_HEADERS.forEach(key => {
      localStorage.removeItem(key);
    });
  };

  /** Initialize on first load */
  useEffect(() => {
    initialize();
  }, []);

  return (
    <AuthContext.Provider value={{
      ...state,
      logIn,
      logOut,
      getProjects,
      checkProject,
      getConfig
    }}>
      {props.children}
    </AuthContext.Provider>
  );
};